import { useMemo, useState } from "react";

import { ErrorMessage } from "@/app/components/error-message";
import { LoadingScreen } from "@/components/loading-screen";
import { SearchBar } from "@/components/search-bar";
import { useGetOrganisations } from "@/features/organisations/api/get-organisations";
import { AddOrganisationModal } from "@/features/organisations/components/add-organisation-modal";
import { OrganisationRowActions } from "@/features/organisations/components/organisation-row-actions";
import { OrganisationsTable } from "@/features/organisations/components/organisations-table";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { PublicOrganisation } from "@classview/shared-types";
import { createColumnHelper } from "@tanstack/react-table";

export function AdminOrganizationsPage() {
  const columnHelper = createColumnHelper<PublicOrganisation>();
  const { data, isLoading, isSuccess, isError, error } = useGetOrganisations();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => {
          return info.getValue();
        },
        header: () => <span>Name</span>,
        enableSorting: true,
      }),
      columnHelper.display({
        id: "actions",
        cell: (info) => (
          <OrganisationRowActions organisationId={info.row.original.id} />
        ),
        size: 70,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [search, setSearch] = useState<string | null>(null);

  const filteredData = useMemo(() => {
    if (!data) return [];

    return data.filter((organisation) =>
      organisation.name.toLowerCase().includes(search?.toLowerCase() ?? ""),
    );
  }, [data, search]);

  return (
    <div>
      <OnboardingHeader
        title="Organisations"
        description="Manage your organisations"
        className="mb-6"
      />

      {isLoading && <LoadingScreen />}
      {isSuccess && (
        <>
          <div className="mb-2 flex flex-col justify-between gap-2 sm:flex-row">
            <SearchBar search={search} setSearch={setSearch} />
            <AddOrganisationModal />
          </div>
          <OrganisationsTable columns={columns} data={filteredData} />
        </>
      )}
      {isError && <ErrorMessage error={error} />}
    </div>
  );
}
